import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, useForm, useFormState } from 'react-final-form';
import {
  AutocompleteInput,
  DateInput,
  ReferenceInput,
  TextInput,
  required,
  regex,
  useNotify,
  useRefresh,
  useUpdate,
} from 'react-admin';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';

import { parseDate } from '../../../helpers';

const validateEmail = regex(
  /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
  'Email invalide',
);

const validatePhoneNumber = regex(
  /^((\+33\s?[1-9](?:\s?\d{2}){4})|(0[1-9](?:\s?\d{2}){4}))$/,
  'Numéro de téléphone invalide',
);

// Keeps `seniority_date` in sync with `contract_start_date` for as long as the
// user has not edited the seniority field by hand. We mirror the start date
// into seniority via `form.change`; a manual edit is detected by comparing the
// current seniority value against the last value we wrote ourselves — if it
// differs (and is not empty), the user took over and we stop mirroring.
const SeniorityDefaultSync = () => {
  const form = useForm();
  const { values } = useFormState({
    subscription: { values: true },
  });
  const lastMirrored = useRef(null);
  const decoupled = useRef(false);

  const start = values.contract_start_date || null;
  const seniority = values.seniority_date || null;

  useEffect(() => {
    // Once the user edits seniority to something other than what we mirrored,
    // stop following the start date.
    if (
      seniority &&
      lastMirrored.current &&
      seniority !== lastMirrored.current
    ) {
      decoupled.current = true;
    }
    if (decoupled.current) {
      return;
    }
    if (start && start !== seniority) {
      lastMirrored.current = start;
      form.change('seniority_date', start);
    }
  }, [start, seniority, form]);

  return null;
};

// "Embaucher" form: moves a vivier person to "Salarié actuel" by PATCHing the
// underlying `cleaner` row (id === person_vivier.id === cleaner_id). All fields
// required. phone/email are pre-filled from the vivier record and editable.
const HireDialog = ({ open, onClose, record }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { loading }] = useUpdate();

  const [error, setError] = useState(null);

  if (!record) {
    return null;
  }

  const cleanerId = record.id;

  const initialValues = {
    manager_id: record.manager_id || null,
    phone: record.phone || record.mobile_phone || '',
    email: record.email || '',
    contract_start_date: null,
    seniority_date: null,
  };

  const handleSubmit = (values) => {
    setError(null);
    const data = {
      id: cleanerId,
      manager_id: values.manager_id,
      phone: values.phone,
      email: values.email,
      contract_start_date: values.contract_start_date,
      seniority_date: values.seniority_date,
      employee_status: 'Salarié actuel',
    };
    return update('cleaner', cleanerId, data, record, {
      onSuccess: () => {
        notify('Personne embauchée — passée en salarié actuel', 'info');
        onClose();
        refresh();
      },
      onFailure: (err) => {
        const message =
          typeof err === 'string'
            ? err
            : (err && err.message) || "Échec de l'embauche";
        setError(message);
        notify(message, 'warning');
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        subscription={{ invalid: true, submitting: true }}
        render={({ handleSubmit: rffSubmit, invalid }) => {
          return (
            // Preventive fix, nothing else changed here (behaviour,
            // validation and message placement are untouched — rendering at
            // normal size is unaffected): `<form>` sits between
            // `.MuiDialog-paper` (flex column, `max-height: calc(100% - 64px)`)
            // and DialogTitle/DialogContent/DialogActions, breaking the flex
            // chain that lets DialogContent (flex: 1 1 auto + overflow-y:
            // auto from its own MUI class) become the scrolling region while
            // DialogActions stays pinned in view. With only 5 fields today
            // the form still fits under the cap, so the defect isn't visible
            // yet — same latent issue actually fixed on EditIdentityDialog
            // (see its `<form>` for the fuller writeup of why `minHeight: 0`
            // is the line that matters).
            <form
              onSubmit={rffSubmit}
              style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: '100%',
              }}
            >
              <DialogTitle>Embaucher</DialogTitle>
              <DialogContent>
                <SeniorityDefaultSync />
                <ReferenceInput
                  fullWidth
                  source="manager_id"
                  label="Manager"
                  reference="cleaner"
                  filterToQuery={(searchText) => {
                    return { fullname: searchText };
                  }}
                  sort={{ field: 'fullname', order: 'ASC' }}
                  filter={{
                    'in(function)': [
                      'Manager',
                      'Admin',
                      'Team Leader',
                      'Office Employee',
                      'Customer Service',
                    ],
                    employee_status: 'Salarié actuel',
                  }}
                  perPage={50}
                  validate={[required()]}
                >
                  <AutocompleteInput optionText="fullname" />
                </ReferenceInput>
                <TextInput
                  fullWidth
                  source="phone"
                  label="Téléphone"
                  validate={[required(), validatePhoneNumber]}
                />
                <TextInput
                  fullWidth
                  source="email"
                  label="E-mail"
                  validate={[required(), validateEmail]}
                />
                <DateInput
                  fullWidth
                  source="contract_start_date"
                  label="Date de début du contrat"
                  parse={parseDate}
                  validate={[required()]}
                />
                <DateInput
                  fullWidth
                  source="seniority_date"
                  label="Date d'ancienneté"
                  parse={parseDate}
                  validate={[required()]}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                  Annuler
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={loading || invalid}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  Embaucher
                </Button>
              </DialogActions>
              {error ? (
                <DialogContent>
                  <span style={{ color: '#f44336' }}>{error}</span>
                </DialogContent>
              ) : null}
            </form>
          );
        }}
      />
    </Dialog>
  );
};

HireDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  record: PropTypes.object,
};

HireDialog.defaultProps = {
  record: undefined,
};

export default HireDialog;
